import { ColumnDef } from '@tanstack/react-table'
import { AddButton } from 'components/CantileverButton'
import { useClickOutside } from 'components/table/enhancements/filters/filterUtils'
import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { RelativeWrap } from 'styles/assetDatabaseStyles'

const Dropdown = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: absolute;
  width: max-content;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.forecast.glassBackground};
  backdrop-filter: blur(32px);
  box-shadow:
    0rem 0.0625rem 0.125rem 0rem ${({ theme }) => theme.forecast.boxShadow},
    0rem 0.25rem 0.5rem 0rem ${({ theme }) => theme.forecast.boxShadow};
  -webkit-backdrop-filter: blur(32px);
`

const DropdownItem = styled.li`
  display: flex;
  min-height: 40px;
  border-radius: 0.5rem;
  align-items: center;
  gap: 0.5rem;
  align-self: stretch;
  cursor: pointer;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:active {
    color: ${({ theme }) => theme.forecast.activeText};
    background-color: ${({ theme }) => theme.forecast.cardBackground};
  }
`

const DropdownItemButton = styled.button`
  width: 100%;
  padding: 0.5rem 1rem;
  text-align: left;
  cursor: pointer;

  &:focus-visible,
  &:focus-within {
    outline: none !important;
    border: 1px solid ${({ theme }) => theme.forecast.border} !important;
  }
`

export const AddFilter = <TData,>({
  columns,
  setActiveFilters,
}: {
  columns: ColumnDef<TData>[]
  setActiveFilters: React.Dispatch<React.SetStateAction<string[]>>
}) => {
  const addFilterRef = useRef<HTMLDivElement>(null)
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false)
  const handleAddFilter = () => {
    setDropdownVisible((prevState) => !prevState)
  }

  const handleSelectFilter = (id: string | undefined) => {
    setDropdownVisible((prevState) => !prevState)
    setActiveFilters((prevActiveFilters) => {
      if (id && !prevActiveFilters.includes(id)) {
        return [...prevActiveFilters, id]
      }
      return prevActiveFilters
    })
  }

  const handleOutsideClick = () => {
    setDropdownVisible(false)
  }

  const handleFocusLeave = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!dropdownVisible) {
      return
    }

    const nextFocusedElement = event.relatedTarget
    if (
      nextFocusedElement instanceof Node &&
      addFilterRef.current?.contains(nextFocusedElement)
    ) {
      return
    }

    setDropdownVisible(false)
  }

  const closeOnEscape = (event: React.KeyboardEvent<HTMLOListElement>) => {
    if (event.key === 'Escape') {
      setDropdownVisible(false)
    }
  }

  useClickOutside(addFilterRef, handleOutsideClick)
  return (
    <RelativeWrap ref={addFilterRef} onBlur={handleFocusLeave}>
      <AddButton
        entityName='filter'
        onClick={handleAddFilter}
        aria-expanded={dropdownVisible}
      />
      {dropdownVisible && (
        <Dropdown role='menu' onKeyDown={closeOnEscape}>
          {columns.map(
            (column) =>
              column.id &&
              column.id !== 'actions' &&
              column.enableColumnFilter !== false && (
                <DropdownItem key={column.id} role='menuitem'>
                  <DropdownItemButton
                    tabIndex={0}
                    onClick={() => handleSelectFilter(column.id)}>
                    {String(column.header)}
                  </DropdownItemButton>
                </DropdownItem>
              )
          )}
        </Dropdown>
      )}
    </RelativeWrap>
  )
}
